<script>
    import { onMount } from "svelte";

    import { isEqual } from "lodash";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        mbsItemsLogic,
        asaItemsLogic,
        mbsToAsaDisallowInsuranceTypeByState,
        amaUnitValue,
        asaAllItemsList,
        mbsAllItemsList,
    } from "../../../stores";

    import {
        SvelteTable,
        NotificationModal,
        CardLayout,
        PageLoading,
    } from "../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getBillingItemCodeType,
        getBillingRuleType,
        getDateForBackend,
        getOOPType,
        getText,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        addIcon,
        editIcon,
        insuranceTypesAllowAsaItem,
        insuranceTypesMayAllowAsaItem,
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
        insuranceTypeHideQuoteSections,
        deleteIcon,
    } from "../../../Utils/utilConstants.svelte";

    import {
        cacheAsaItems,
        cacheMbsItems,
    } from "../../../Utils/utilCache.svelte";

    import {
        billingRuleAllowed,
        generateLatestBillingDetails,
        getASAAmaUnits,
        getConsultTimeItemNote,
        getMBSRebateUnits,
        getSumOfColumnValues,
        syncBillingStatus,
        syncLatestBillingDataTime,
        updateEncounterLatestBillingData,
    } from "../helpers/encounterHelper.svelte";

    import CodeSelectorMbs from "./codeSelector/CodeSelectorMBS.svelte";
    import CodeSelectorAsa from "./codeSelector/CodeSelectorASA.svelte";
    import CodeSelectorFreeText from "./codeSelector/CodeSelectorFreeText.svelte";

    import PatientEncounterModifyItemModal from "./PatientEncounterModifyItemModal.svelte";
    import PatientEncounterAddBillingRuleModal from "./PatientEncounterAddBillingRuleModal.svelte";

    import { fetchAndSyncItemsRebate } from "../helpers/mbsHelper.svelte";

    import {
        DirectInsuranceType,
        estimateStatus,
    } from "../../../Utils/utilEnums.svelte";
    import { getSelectedPresetApplicableRuleForEncounter } from "../../Presets/helpers/presetsHelper.svelte";
    import InfoIcon from "../../../Components/Common/InfoIcon.svelte";

    export let patientEncounterDetails = {};
    export let latestObjDetails = {};
    export let itemsData = []; // mbsData or asaData
    export let billingData = []; // quotesData or invoicesData
    export let __BackupItemsData__ = [];
    export let isRegenerating = false;
    export let itemType = "";
    export let itemsNotSaved = false;
    export let scenarioOneFulfilled = false;
    export let scenarioTwoFulfilled = false;
    export let showSelectBillingRule = false;
    export let isQuoteSectionsHidden = false;
    export let paymentsData = [];
    export let doctorPresets = [];
    export let doctorProviders = [];
    export let doctorBillingRules = [];
    export let reloadBillingDetails = () => {};
    export let regenerateQuoteOrInvoice = () => {};
    export let reloadBillingDetailsAndUpdateDefaultItems = () => {};

    export let latestQuoteData = {};
    export let latestQuoteDataItems = [];

    $: itemsNotSaved = !isEqual(itemsData, __BackupItemsData__);

    $: if (itemsNotSaved) displayItemsData = _syncLatestDataItemsWithLogic();

    $: isBillingDetailsUsingAsa =
        latestObjDetails?.billingCodesType &&
        getBillingItemCodeType(latestObjDetails.billingCodesType) == "ASA";

    $: isBillingDetailsUsingFreeText =
        latestObjDetails?.billingCodesType &&
        getBillingItemCodeType(latestObjDetails.billingCodesType) == "FreeText";

    $: allowAsaCodes =
        _checkAllowAsaCodes(itemType, patientEncounterDetails) && !isTypeA;

    $: allowFreeTextCodes =
        patientEncounterDetails?.patient?.insuranceType &&
        [
            DirectInsuranceType.Cosmetic,
            DirectInsuranceType.ThirdPartyInsurer,
        ].includes(patientEncounterDetails.patient.insuranceType);

    $: if (itemType == "invoice")
        // if quotes related sections are not hidden
        // copy items if no valid latest invoice details
        // copy billing rule if latest invoice choose same itemType (MBS or ASA) as latest quote does && no valid billing rule selected
        shouldInvoiceCopyQuote =
            (!isQuoteSectionsHidden &&
                isObjectEmpty(latestObjDetails) &&
                !isObjectEmpty(latestQuoteData)) ||
            (!latestObjDetails.billingType &&
                latestQuoteData.billingType &&
                latestObjDetails.billingCodesType ==
                    latestQuoteData.billingCodesType);

    $: columns = isBillingDetailsUsingFreeText
        ? [...FreeTextColumns]
        : isBillingDetailsUsingAsa
          ? [...AsaColumns]
          : [...MbsColumns];

    $: showAddItemIcon = !isObjectEmpty(latestObjDetails);

    $: hasActiveQuoteOrInvoice = itemType == "invoice" ? 
        patientEncounterDetails.invoices && patientEncounterDetails.invoices.length && patientEncounterDetails.invoices.some((e) => e.status != 9) :
        patientEncounterDetails.estimates && patientEncounterDetails.estimates.length && patientEncounterDetails.estimates.some((e) => e.status != 9)

    // property determined to hide switch item type between mbs & asa when related quote/invoice is valid
    $: hasValidBillingData =
        billingData.length &&
        billingData.some(
            (bd) =>
                bd.patientEncounterBillingDataId == latestObjDetails.id &&
                bd.status != 9,
        );

    $: encounterHasNoProvider =
        !patientEncounterDetails.doctorProviderId ||
        !patientEncounterDetails.doctorProvider;

    $: showSelectBillingRule = !btnOptions[2].isHidden;

    $: showSkipQuoteIcon = itemType == "quote";

    $: hideHeaderIconsBasedOnSkipQuote =
        showSkipQuoteIcon && !!patientEncounterDetails?.skipQuote;
    
    $: isQuoteAndSubmittedStatusOrHigher = itemType == "quote" && [2,3,4,6].includes(patientEncounterDetails.billingStatus);

    $: btnOptions = [
        {
            iconName: "skipQuoteIcon",
            svgIcon: `<?xml version="1.0" encoding="UTF-8"?>
                    <svg fill="#FFFFFF" width="24px" height="24px" version="1.1" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <g>
                    <path d="m33.332 68.918 10.898-9.8555v-18.125l-10.918-9.875z"/>
                    <path d="m48.395 68.957 27.586-18.957-27.586-18.957z"/>
                    <path d="m50 8.332c-11.051 0-21.648 4.3906-29.461 12.207-7.8164 7.8125-12.207 18.41-12.207 29.461s4.3906 21.648 12.207 29.461c7.8125 7.8164 18.41 12.207 29.461 12.207s21.648-4.3906 29.461-12.207c7.8164-7.8125 12.207-18.41 12.207-29.461s-4.3906-21.648-12.207-29.461c-7.8125-7.8164-18.41-12.207-29.461-12.207zm28.332 45.105-27.582 18.957c-0.69141 0.47656-1.5156 0.73047-2.3555 0.73047-0.67188 0-1.3398-0.16406-1.9375-0.48047-1.3672-0.71875-2.2266-2.1367-2.2266-3.6875v-4.375l-8.332 7.543h-0.003907c-0.058593 0.066406-0.12891 0.12109-0.20703 0.16797-0.67969 0.51172-1.5039 0.80078-2.3555 0.83203-0.67188-0.003906-1.3359-0.16797-1.9375-0.48047-1.3672-0.71875-2.2266-2.1367-2.2266-3.6875v-37.914c0.007812-1.543 0.86719-2.957 2.2305-3.6719 1.3672-0.71484 3.0156-0.61719 4.2891 0.25391 0.078125 0.042969 0.14844 0.10156 0.20703 0.16797l8.332 7.543 0.003907-4.293c0-1.5469 0.85547-2.9648 2.2227-3.6875 1.3672-0.71875 3.0195-0.625 4.2969 0.25l27.582 18.957c1.1367 0.77734 1.8125 2.0625 1.8125 3.4375s-0.67578 2.6602-1.8125 3.4375z"/>
                    </g>
                    </svg>`,
            btnText: patientEncounterDetails?.skipQuote
                ? "Quote Skipped"
                : "Skip Quote",
            hasTooltip: patientEncounterDetails?.skipQuote,
            tooltip: "Click here to requote the patient",
            isHidden: !showSkipQuoteIcon || isQuoteAndSubmittedStatusOrHigher,
            iconFunction: () => _openSkipQuoteConfirmModal(),
        },
        {
            iconName: "selectPresetIcon",
            iFont: "fa-solid fa-file-signature",
            iconFunction: () => _openSelectPresetModal(),
            btnText: "Apply Preset",
            // hide preset icon if billingObj has generated estimate/invoice pdf OR
            // encounter billing status is other than 0 OR
            // encounter billing status is 2 but ALL quote-related sections are hidden
            isHidden:
                isQuoteAndSubmittedStatusOrHigher ||
                hideHeaderIconsBasedOnSkipQuote ||
                hasValidBillingData ||
                patientEncounterDetails.billingStatus > 2 ||
                (patientEncounterDetails.billingStatus == 2 &&
                    (
                        !insuranceTypeHideQuoteSections.includes(
                            patientEncounterDetails?.patient?.insuranceType,
                        ) 
                        && 
                        (patientEncounterDetails?.patient?.insuranceType != 0 && patientEncounterDetails?.patient?.insuranceId != 13)
                    ) 
                ), //this to show for doctors healthfund top cover (whih has only invoice and status 2 (Ready))
        },
        {
            iconName: "addIcon",
            iconFunction: () => _openModifyItemModal(),
            hasTooltip: true,
            isHidden:  hideHeaderIconsBasedOnSkipQuote || !showAddItemIcon || isQuoteAndSubmittedStatusOrHigher,
            btnText: __BackupItemsData__.length
                ? isBillingDetailsUsingFreeText
                    ? "Change Items"
                    : "Select Items"
                : "Add",
            iFont: __BackupItemsData__.length ? editIcon : addIcon,
            tooltip: `${__BackupItemsData__.length ? (isBillingDetailsUsingFreeText ? "Change" : "Select") : "Add"} ${isBillingDetailsUsingAsa ? "ASA" : isBillingDetailsUsingFreeText ? "Free Text" : "MBS"} Items`,
        },
        {
            iconName: "addWithoutBillingDataIcon",
            iFont: addIcon,
            iconFunction: () => _openAddItemModalWithoutLatestBillingData(),
            hasTooltip: true,
            tooltip: "Add Items",
            btnText: "Add",
            isHidden: hideHeaderIconsBasedOnSkipQuote || showAddItemIcon || isQuoteAndSubmittedStatusOrHigher,
        },
        {
            iconName: "billingRuleIcon",
            iFont: "fa-solid fa-wallet",
            iconFunction: () => _openAddBillingRuleModal(),
            hasTooltip: encounterHasNoProvider,
            tooltip:
                "No provider has been set to this encounter, please add one before select billing rule",
            btnText: "Select billing rule",
            isHidden: 
                isQuoteAndSubmittedStatusOrHigher ||
                hideHeaderIconsBasedOnSkipQuote ||
                isBillingDetailsUsingFreeText ||
                (__BackupItemsData__.length && !itemsNotSaved
                    ? !billingRuleAllowed(latestObjDetails)
                    : true),
            isDisabled: encounterHasNoProvider,
        },
    ];

    let isModifyItemModalOpen = false,
        isAddItemModalWithoutLatestBillingDataModalOpen = false,
        isEditItemModalOpen = false,
        isAddBillingRuleModalOpen = false,
        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            itemType: "",
        },
        defaultItem = {},
        selectedItem = {};

    let billingRuleModalRef;

    let displayItemsData = [],
        mbsTimeData = [];

    let isShowRegenerateModal = false;

    let isShowDeleteModal = false,
        isDeletingItem = false;

    let isUpdatingItems = false;

    // typeA - MBS items ONLY, not show billing selector
    // typeB - default ASA, enable switch item types, show billing selector
    let isTypeA = false;

    let shouldInvoiceCopyQuote = false,
        copyLatestQuoteBillingRule = {};

    let isConfirmActionModalOpen = false,
        confirmAction = () => {};

    let isSelectPresetModalOpen = false,
        selectedPreset = {},
        selectedPresetEncounterRule = {},
        isConfirmSelectPresetModalOpen = false,
        isApplyingPreset = false;

    let toggleSkipQuoteModalOpen = false,
        isChangingSkipQuote = false;

    onMount(async () => {
        isTypeA = await _checkInsuranceTypeIsTypeA();

        setTimeout(() => {
            displayItemsData = _syncLatestDataItemsWithLogic();
        }, 0);

    });

    $: MbsColumns = [
        {
            key: "item",
            title: "Item",
            headerClass: "text-left pl-3 cursor-default w-[10%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.itemNum)}</p>`,
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoType),
        },
        {
            key: "title",
            title: "Title",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoShortTitle),
        },
        {
            key: "unitValue",
            title: "Unit value",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            // renderValue: (v) =>
            //     `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.rebateBasicUnits)}</p>`,
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(getMBSRebateUnits(v))}</p>`,
        },
        {
            key: "benefit100",
            title: "MBS-100", // renamed Benefit100 - Medicare Rebate
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.benefit100) : getAmountInCurrency(v.benefit100)}</p>`,
        },
        {
            key: "rebate",
            title: "Rebate", // renamed Rebate - Insurer Rebate
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.rebate) : getAmountInCurrency(v.rebate)}</p>`,
        },
        {
            key: "feeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "itemNote",
            title: "Item Note",
            headerClass: "text-left px-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) => getText(v.encounterItemDescription),
            isHidden: itemType != "invoice",
        },
        {
            key: "actions",
            title: "",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                v.isLast ? "" : _renderActionIcons(itemType, v),
        },
    ];

    $: AsaColumns = [
        {
            key: "item",
            title: "Item",
            headerClass: "text-left pl-3 cursor-default w-[10%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.itemCode)}</p>`,
        },
        {
            key: "mbsItemCode",
            title: "MBS Code",
            headerClass: "text-left px-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                v.mbsItemNum ? getText(v.mbsItemNum) : v.isLast ? "" : "-",
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoType),
        },
        {
            key: "title",
            title: "Title",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoShortTitle),
        },
        {
            key: "amaUnits",
            title: "AMA Units",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(getASAAmaUnits(v))}</p>`,
        },
        {
            key: "rebateAMA",
            title: "Insurer Rebate", // renamed Rebate
            headerClass: "text-left px-3 cursor-default w-[10%] ",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.rebate) : getAmountInCurrency(v.rebate)}</p>`,
        },
        {
            key: "feeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "itemNote",
            title: "Item Note",
            headerClass: "text-left px-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) => getText(v.encounterItemDescription),
            isHidden: itemType != "invoice",
        },
        {
            key: "actions",
            title: "",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) =>
                v.isLast ? "" : _renderActionIcons(itemType, v),
        },
    ];

    $: FreeTextColumns = [
        {
            key: "freeTextDescription",
            title: "Item Description",
            headerClass: "text-left pl-3 cursor-default w-[70%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[70%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? "Total" : getText(v.encounterItemDescription)}</p>`,
        },
        {
            key: "freeTextFeeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        }
    ];

    const saveBillingItem = async (
        item,
        actionType = "add",
        itemProps,
        timeObj,
    ) => {
        switch (actionType) {
            case "add":
                isUpdatingItems = true;

                const { billingData } = patientEncounterDetails;

                const { neuraxialType, isUsingAsaItems, isUsingFreeTextItems } =
                    itemProps;

                // Auto generate latest billing data if none
                if (
                    !billingData.length ||
                    (itemType == "invoice" &&
                        !billingData.some((bd) => bd.isLatestInvoiceData)) ||
                    (itemType == "quote" &&
                        !billingData.some((bd) => bd.isLatestQuoteData))
                ) {
                    const billingCodesType = isUsingFreeTextItems
                        ? Number(2)
                        : isUsingAsaItems
                          ? Number(1)
                          : Number(0);

                    patientEncounterDetails =
                        await generateLatestBillingDetails(
                            patientEncounterDetails,
                            itemType,
                            billingCodesType,
                            timeObj,
                        );

                    await apiMethods.putMethod
                        .updatePatientEncounter(patientEncounterDetails, false, "Save billing item")
                        .then((res) => reloadBillingDetails(false, res));

                    // update itemProps billingDataId as in this case the billingDataId was undefined
                    itemProps.billingDataId = latestObjDetails.id;
                }

                if (isUsingFreeTextItems) {
                    await updateFreeTextItems(item, itemProps, timeObj);
                    await confirmRegenerate({
                        isRecalculate: false,
                        isItemChange: false,
                        skipRegenerateAndCalculate: true,
                        oldLatestObjDetails: latestObjDetails,
                    });
                }
                else {
                    if (!isUsingAsaItems)
                        item = await fetchAndSyncItemsRebate(item);

                    await _handleAddItem(item, itemDetails);

                    patientEncounterDetails.neuraxialType = neuraxialType;

                    await updateItems(timeObj, itemProps);
                }

                isUpdatingItems = false;

                _closeModifyItemModal();

                break;
            case "edit":
                await _handleEditItem(item);

                _closeEditItemModal();
                break;
            case "delete":
                await _handleDeleteItem(item);

                _closeDeleteModal();
                break;
            default:
                isUpdatingItems = false;
                return;
        }
    };

    const saveBillingRule = () => billingRuleModalRef.updateBillingRule();

    const handleSelectItemToAdd = (type = "") => {
        isBillingDetailsUsingAsa = type == "ASA";
        isBillingDetailsUsingFreeText = type == "FreeText";

        _closeAddItemModalWithoutLatestBillingData();
        _openModifyItemModal();
    };

    export const updateItems = async (timeObj = {}, itemProps) => {
        if (isBillingDetailsUsingAsa) {
            latestObjDetails.asaData = itemsData
                .filter((item) => item.itemCode != "Time")
                .map((item) => {
                    delete item.id;
                    return {
                        ...item,
                        // encounterItemDescription: "",
                        amaCode: item.itemCode,
                    };
                });
            if (itemsData.some((item) => item.itemCode == "Time")) {
                const timeCode = itemsData.find(
                    (item) => item.itemCode == "Time",
                );

                latestObjDetails.asaTimeData = [{ ...timeCode }];
            } else latestObjDetails.asaTimeData = [];
        } else latestObjDetails.mbsData = [...itemsData];

        // sync start & end time from select item modal
        latestObjDetails = syncLatestBillingDataTime(
            latestObjDetails,
            timeObj,
            patientEncounterDetails.scheduledDate,
        );

        // sync billingType for insuranceType not allow select billing rule to get fee
        if(patientEncounterDetails.isNoGapProgram) {
            latestObjDetails.billingType = 3;
            latestObjDetails.calculationAmount = 0;
        } else {
            if (!billingRuleAllowed(latestObjDetails))
                latestObjDetails.billingType = 3;
        }

        patientEncounterDetails.billingData.map((bd) => {
            if (bd.id == latestObjDetails.id) {
                if (itemProps.customDueDate)
                    latestObjDetails.customDueDate = getDateForBackend(
                        itemProps.customDueDate,
                    );

                return latestObjDetails;
            } else return bd;
        });

        isUpdatingItems = true;

        if (itemProps.claimDelayHours || itemProps.claimDelayHours == 0)
            patientEncounterDetails.claimDelayHours = itemProps.claimDelayHours;

        await apiMethods.putMethod
            .updatePatientEncounter({ ...patientEncounterDetails }, true, "Update items")
            .then(async (res) => {
                if (res.error) showError(res.error);
                else await handleOpenModal(true, res);
            })
            .catch((err) => showException(err));

        __BackupItemsData__ = [...itemsData];

        isUpdatingItems = false;
    };

    const updateFreeTextItems = async (
        items = [],
        itemProps = {},
        timeObj = {},
    ) => {
        const { billingDataId } = itemProps;

        const existingItems = __BackupItemsData__;
        const itemsMap = new Map(items.map((item) => [item.id, item]));

        let needsPreReload = false;

        // handle remove items, for the ones that do not exist in the backup
        const deletedItems = existingItems.filter(
            (backupItem) => !itemsMap.has(backupItem.id),
        );
        if (deletedItems.length > 0)
            for (const item of deletedItems) {
                needsPreReload = true;
                await apiMethods.deleteMethod
                    .deleteFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to delete free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });
            }
               

        // handle new items
        const newItems = items.filter((item) => item.hasOwnProperty("tempId"));
        if (newItems.length > 0)
            for (const item of newItems){
                needsPreReload = true;
                await apiMethods.postMethod
                    .createFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        item.id = res.id; //set item id, to be stored on backup data
                        item.tempId = null; //remove temp id

                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to create free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });
            }
                

        // handle update items
        const modifiedItems = items.filter((item) => {
            const backupItem = existingItems.find(
                (backup) => backup.id === item.id,
            );
            return (
                backupItem &&
                (backupItem.encounterItemDescription !=
                    item.encounterItemDescription ||
                    backupItem.feeCharged != item.feeCharged)
            );
        });
        if (modifiedItems.length > 0)
            for (const item of modifiedItems) {
                needsPreReload = true;
                await apiMethods.putMethod
                    .updateFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to update free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });

            }
              
        if(needsPreReload)
            await reloadBillingDetails(); 
        
        const { startTime, endTime } = timeObj;
        
        let newStartDate, newEndDate, hasValueChanged = false;
        if (startTime && patientEncounterDetails.scheduledDate) {
            newStartDate = `${patientEncounterDetails.scheduledDate.split("T")[0]}T${startTime}`;
        }
        if (endTime && patientEncounterDetails.scheduledDate) {
            newEndDate = `${patientEncounterDetails.scheduledDate.split("T")[0]}T${endTime}`;
        }
        if (newStartDate && newStartDate != latestObjDetails.startDate) {
            latestObjDetails.startDate = newStartDate;
            hasValueChanged = true;
        }
        if (newEndDate && newEndDate != latestObjDetails.endDate) {
            latestObjDetails.endDate = newEndDate;
            hasValueChanged = true;
        }
        if(itemDetails.oopType != latestObjDetails.oopType) {
            latestObjDetails.oopType = itemDetails.oopType;
            hasValueChanged = true;
        }

        if(hasValueChanged) {
            await apiMethods.putMethod
                .updatePatientEncounter(patientEncounterDetails, false, "Save Free Text - other details (ooptype/start time/end time)")
                .then(async (res) => {
                    if (!res || res.error) {
                        showError(
                            res.error ??
                                "Failed to update encounter date, please refresh the page and try again and contact support if the issue persists.",
                        );
                        await reloadBillingDetails();
                    } else await reloadBillingDetails(false, res);
                });
        }

        __BackupItemsData__ = [...items];
        displayItemsData = _syncLatestDataItemsWithLogic();
    };

    const handleOpenModal = async (isItemChange = false, response = {}) => {
        if (encounterHasNoProvider) return;

        const oldLatestObjDetails = latestObjDetails;

        await reloadBillingDetails(false, response);

        const { insuranceType, billingType } = latestObjDetails;

        if (
            insuranceTypesNotAllowBillingRule.includes(insuranceType) ||
            billingType
        )
            await confirmRegenerate({
                isRecalculate: true,
                isItemChange,
                oldLatestObjDetails,
            });
        else {
            // Sync display items to match api calculation result
            displayItemsData = _syncLatestDataItemsWithLogic();
            displayItemsData = await _syncAsaTimeData(displayItemsData);

            await regenerateQuoteOrInvoice(
                latestObjDetails,
                true,
                itemType,
                isItemChange,
                true,
                oldLatestObjDetails,
            );

            !isTypeA && _openAddBillingRuleModal();
        }

        // await updateBillingRuleTotalFee(
        //     patientEncounterDetails,
        //     latestObjDetails,
        // );

        await reloadBillingDetails(false, patientEncounterDetails);
    };

    const confirmRegenerate = async ({
        isRecalculate = false,
        isItemChange = false,
        skipRegenerateAndCalculate = false,
        oldLatestObjDetails = {},
    }) => {
        await regenerateQuoteOrInvoice(
            latestObjDetails,
            isRecalculate,
            itemType,
            isItemChange,
            skipRegenerateAndCalculate,
            oldLatestObjDetails,
        );

        if (itemType == "invoice")
            await syncBillingStatus(patientEncounterDetails);

        // Sync display items to match api calculation result
        displayItemsData = _syncLatestDataItemsWithLogic();
        displayItemsData = await _syncAsaTimeData(displayItemsData);

        _closeRegenerateModal();
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            selectedItem = { ...rowData };

            if (className.includes("addIcon")) _openModifyItemModal(false);
            else if (className.includes("deleteIcon")) _openDeleteModal();
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const confirmChangeItemsOrBillingRules = () => {
        confirmAction();
        _closeConfirmActionModal();
    };

    const changeItemType = async (billingCodesType = Number(0)) => {
        isBillingDetailsUsingAsa = billingCodesType == 1;
        isBillingDetailsUsingFreeText = billingCodesType == 2;

        _closeModifyItemModal();

        if (shouldInvoiceCopyQuote) {
            itemsData = [];

            _openModifyItemModal(true);

            return;
        }

        // update billing data if exists
        if (latestObjDetails.id) {
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (
                        bd.id == latestObjDetails.id &&
                        ((itemType == "quote" && bd.isLatestQuoteData) ||
                            (itemType != "quote" && bd.isLatestInvoiceData))
                    ) {
                        const calcAmount = isBillingDetailsUsingAsa ? $amaUnitValue : 0;
                         return {
                            ...bd,
                            asaData: [],
                            mbsData: [],
                            freeTextData: [],
                            asaTimeData: null,
                            actualGap: Number(0),
                            rebate: Number(0),
                            totalFee: Number(0),
                            billingType: 2, //unit value always
                            calculationAmount: calcAmount,
                            usingAmaRates: isBillingDetailsUsingAsa ? true : false,
                            useAsaCodes: isBillingDetailsUsingAsa,
                            billingCodesType,
                        };
                    }
                    else return bd;
                });

            patientEncounterDetails = updateEncounterLatestBillingData(
                patientEncounterDetails,
            );

            // reset Labour Epidural neuraxialType when change item type
            patientEncounterDetails.neuraxialType = 0;

            await apiMethods.putMethod
                .updatePatientEncounter(patientEncounterDetails, false, "Change items type")
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            `Failed to change item type, please refresh the page and try again and contact support if the issue persists.`,
                        );
                    else await reloadBillingDetailsAndUpdateDefaultItems();
                })
                .catch((err) => showException(err));
        }

        _openModifyItemModal();
    };

    const clearBillingData = async () => {
        // update billing data if exists
        if (latestObjDetails.id) {
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (
                        bd.id == latestObjDetails.id &&
                        ((itemType == "quote" && bd.isLatestQuoteData) ||
                            (itemType != "quote" && bd.isLatestInvoiceData))
                    )
                        return {
                            ...bd,
                            asaData: [],
                            mbsData: [],
                            freeTextData: [],
                            asaTimeData: null,
                            actualGap: Number(0),
                            rebate: Number(0),
                            totalFee: Number(0),
                            billingType: 2, //unit value always
                            calculationAmount: isBillingDetailsUsingAsa ? 110 : 0
                        };
                    else return bd;
                });

            patientEncounterDetails = updateEncounterLatestBillingData(
                patientEncounterDetails,
            );

            // reset Labour Epidural neuraxialType when clearing items
            patientEncounterDetails.neuraxialType = 0;
            try {
                var res = await apiMethods.putMethod.updatePatientEncounter(patientEncounterDetails, false, `Clear ${itemType} items`);
                if (!res || res.error) {
                    showError(
                        `Failed to clear ${itemType} items, please refresh the page and try again and contact support if the issue persists.`,
                    );
                    return false;
                }
                else await reloadBillingDetailsAndUpdateDefaultItems();

                return true;
            }
            catch(err) {
                showException(err);
            }
        }

        return true;
    };

    const handleSelectPreset = async () => {
        // show confirm modal if billing data has mbs items already
        if (latestObjDetails.id) {
            const hasMbsItems =
                latestObjDetails.mbsData &&
                latestObjDetails.mbsData.length > 0;

            if (hasMbsItems && !selectedPreset.useAsaCodes) {
                _openConfirmSelectPresetModal();
                return;
            }

            const hasAsaItems =
                latestObjDetails.asaData &&
                latestObjDetails.asaData.length > 0;
            if (hasAsaItems && selectedPreset.useAsaCodes) {
                _openConfirmSelectPresetModal();  
                return; 
            }
        }
        await confirmSelectPreset();
    };

    const confirmSelectPreset = async () => {
        isApplyingPreset = true;

        await apiMethods.putMethod
            .applyPresetToEncounter(
                patientEncounterDetails.id,
                latestObjDetails.id,
                selectedPreset.id,
            )
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to apply preset, please refresh the page and try again and contact support if the issue persists.",
                    );
            });

        isApplyingPreset = false;

        await reloadBillingDetailsAndUpdateDefaultItems();

        displayItemsData = _syncLatestDataItemsWithLogic();

        _closeSelectPresetModal();
    };

    const confirmToggleSkipQuote = async () => {
        if (isChangingSkipQuote) return;

        isChangingSkipQuote = true;

        await apiMethods.putMethod.updatePatientEncounter({
            ...patientEncounterDetails,
            skipQuote: !patientEncounterDetails.skipQuote,
            billingStatus: patientEncounterDetails.skipQuote
                ? 0
                : Math.max(2, patientEncounterDetails.billingStatus),
        }, false, "Confirm skip quote");

        await reloadBillingDetails();

        isChangingSkipQuote = false;

        _closeSkipQuoteConfirmModal();
    };

    // HELPER FUNCTIONS
    const _handleAddItem = async (items = [], itemDetails) => {
        items = items.map((item) => ({
            ...item,
            feeCharged: itemDetails.isUsingAsaItems
                ? item.rebateAMA
                : item.benefit100,
            rebate: itemDetails.isUsingAsaItems
                ? item.rebateAMA
                : item.benefit100,
            // fieldChange: 0,
            encounterItemDescription: getConsultTimeItemNote(item, itemDetails),
        }));

        itemsData = [...items];
    };

    const _handleEditItem = async (item) => {
        const {
            isUsingAsaItems,
            isUsingFreeTextItems,
            encounterItemDescription,
        } = item;

        const { id: encounterId } = patientEncounterDetails;

        const { id: billingDataId } = latestObjDetails;

        isUpdatingItems = true;

        if (isUsingAsaItems) {
            const { itemCode, subItemCode } = item;

            const data = {
                encounterId,
                billingDataId,
                itemCode,
                subItemCode,
                encounterItemDescription,
            };

            await apiMethods.putMethod
                .updateEncounterAsaItemDescription(data)
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        } else if (isUsingFreeTextItems)
            await apiMethods.putMethod
                .updateFreeTextItem({
                    ...item,
                    billingCodesType: Number(2),
                    patientEncounterId: encounterId,
                    patientEncounterBillingDataId: billingDataId,
                })
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update selected item, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                        displayItemsData = _syncLatestDataItemsWithLogic();
                    }
                })
                .catch((err) => showException(err));
        else {
            const { itemNum, subItemNum } = item;

            const data = {
                encounterId,
                billingDataId,
                itemNum,
                subItemNum,
                encounterItemDescription,
            };

            await apiMethods.putMethod
                .updateEncounterMbsItemDescription(data)
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        }

        isUpdatingItems = false;
    };

    const _handleDeleteItem = async (item) => {
        isDeletingItem = true;

        const isUsingFreeTextItems =
            item.id &&
            !item.hasOwnProperty("itemCode") &&
            !item.hasOwnProperty("itemNum");

        if (isUsingFreeTextItems) {
            await apiMethods.deleteMethod
                .deleteFreeTextItem({
                    ...item,
                    billingCodesType: Number(2),
                    patientEncounterId: patientEncounterDetails.id,
                    patientEncounterBillingDataId: latestObjDetails.id,
                })
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to remove selected item, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        } else {
            const isAsaItem = item.hasOwnProperty("itemCode");

            if (!isAsaItem)
                itemsData = itemsData.filter(
                    (data) =>
                        data.itemNum != item.itemNum ||
                        data.subItemNum != item.subItemNum,
                );
            else
                itemsData = itemsData.filter(
                    (data) =>
                        data.itemCode != item.itemCode ||
                        data.amaCode != item.amaCode,
                );

            if (isAsaItem && item.itemCode == "Time") {
                patientEncounterDetails.billingData =
                    patientEncounterDetails.billingData.map((bd) => {
                        if (bd.id != latestObjDetails.id) return { ...bd };
                        else return { ...bd, asaTimeData: null };
                    });
            }
        }

        isDeletingItem = false;
    };

    const _syncLatestDataItemsWithLogic = () => {
        const itemMap = {};
        const updatedItemsData = itemsData.map((item) => ({ ...item }));

        if (isBillingDetailsUsingAsa) {
            for (const mapping of $asaItemsLogic) {
                itemMap[mapping.hideItem] = mapping.targetItem;
            }

            return updatedItemsData.filter((item) => {
                const hideItem = item.itemCode?.toString();
                if (itemMap[hideItem] !== undefined) {
                    const targetItem = updatedItemsData.find(
                        (item) => item.itemCode === itemMap[hideItem],
                    );
                    if (targetItem) {
                        targetItem.feeCharged += item.feeCharged;
                        targetItem.rebate += item.rebate;
                        return false;
                    }
                    return true;
                }
                return true;
            });
        } else {
            for (const mapping of $mbsItemsLogic) {
                itemMap[mapping.hideItem] = mapping.targetItem;
            }

            return updatedItemsData.filter((item) => {
                const hideItem = item.itemNum?.toString();
                if (itemMap[hideItem] !== undefined) {
                    const targetItem = updatedItemsData.find(
                        (item) => item.itemNum === itemMap[hideItem],
                    );
                    if (targetItem) {
                        targetItem.benefit100 += item.benefit100;
                        targetItem.feeCharged += item.feeCharged;
                        targetItem.rebate += item.rebate;
                        return false;
                    }
                    return true;
                }
                return true;
            });
        }
    };

    const _syncAsaTimeData = async (arr) => {
        if (!arr.some((item) => item.itemCode == "Time")) return arr;

        if (!mbsTimeData.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "Time",
                })
                .then((res) => {
                    if (res.results?.length) mbsTimeData = [...res.results];
                });

        const timeItem = arr.find((item) => item.itemCode == "Time");

        const targetItem = mbsTimeData.find(
            (td) => td.basicUnits == timeItem.units,
        );

        if (targetItem) timeItem.mbsItemNum = targetItem.itemNum;

        return arr.map((item) => {
            if (item.itemCode == "Time") return timeItem;
            else return item;
        });
    };

    const _renderActionIcons = (itemType = "", data) => {
        if (isBillingDetailsUsingFreeText)
            return `<div class="flex flex-1 items-center justify-start gap-x-2">
                        <div class="addIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit item"}">
                            <i class="addIcon ${editIcon}"></i>
                        </div>

                        <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove item"}">
                            <i class="deleteIcon ${deleteIcon}"></i>
                        </div>
                    </div>`;

        if (!itemType || itemType.toLowerCase() == "quote") return "";

        const { encounterItemDescription } = data;

        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="addIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="${encounterItemDescription ? "Edit" : "Add"} item note">
                        ${
                            encounterItemDescription
                                ? `
                            <i class="addIcon ${editIcon}"></i>
                        `
                                : `
                            <i class="addIcon ${addIcon}"></i>
                        `
                        }
                    </div>
                </div>`;
    };

    const _resetDefaultBillingItem = () => {
        const patientAge = _getPatientAge();

        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            encounterId: patientEncounterDetails.id,
            billingDataId: latestObjDetails.id,
            oopType: latestObjDetails.oopType,
            isUsingAsaItems: isBillingDetailsUsingAsa,
            isUsingFreeTextItems: isBillingDetailsUsingFreeText,
            billingType: patientEncounterDetails.typeOfBill,
            itemType,
            patientAge,
            patientState: patientEncounterDetails.patient?.state ?? "",
            neuraxialType: patientEncounterDetails.neuraxialType,
            mbsItem22012UsedToday:
                patientEncounterDetails.mbsItem22012UsedToday,
            mbsItem22014UsedToday:
                patientEncounterDetails.mbsItem22014UsedToday,
            mbsItem18216UsedToday: patientEncounterDetails.mbsItem18216UsedToday,
            mbsItem18226UsedToday: patientEncounterDetails.mbsItem18226UsedToday,
            mbsItem17680UsedToday: patientEncounterDetails.mbsItem17680UsedToday,
            scheduledDate: patientEncounterDetails.scheduledDate,
            insuranceType: latestObjDetails.insuranceType,
            insuranceState: latestObjDetails.patientInsuranceState,
            claimDelayHours: Number(0),
            customDueDate: null,
            showClaimDelayHoursAndInvoiceCustomDueDate: false,
            procedureName: patientEncounterDetails.procedureName,
            hospital: null,
        };
        defaultItem = {};
        selectedItem = {};
    };

    const _openModifyItemModal = (
        isNew = true,
        openModalAfterModify = true,
    ) => {
        const patientAge = _getPatientAge();

        if (isNew) {
            const isWcOrTacInsurance =
                insuranceTypesMayAllowAmaBillingRule.includes(
                    latestObjDetails.insuranceType,
                );
            const isWcOrTacUsingAmaRates =
                latestObjDetails.usingAmaRates ?? true;
            const shouldUseDynamicAmaRates =
                isWcOrTacInsurance && isWcOrTacUsingAmaRates;

            const {
                patient: { insuranceType, insuranceState },
                procedureName,
                hospital,
            } = patientEncounterDetails;

            itemDetails = {
                ...itemDetails,
                encounterId: patientEncounterDetails.id,
                billingDataId: latestObjDetails.id,
                isUsingAsaItems: isBillingDetailsUsingAsa,
                isUsingFreeTextItems: isBillingDetailsUsingFreeText,
                billingType: patientEncounterDetails.typeOfBill,
                oopType: latestObjDetails.oopType,
                itemType,
                patientAge,
                patientState: patientEncounterDetails.patient?.state ?? "",
                neuraxialType: patientEncounterDetails.neuraxialType,
                shouldUseDynamicAmaRates,
                currentCalculationAmount:
                    latestObjDetails.calculationAmount ?? $amaUnitValue,
                mbsItem22012UsedToday:
                    patientEncounterDetails.mbsItem22012UsedToday,
                mbsItem22014UsedToday:
                    patientEncounterDetails.mbsItem22014UsedToday,
                mbsItem18216UsedToday:
                    patientEncounterDetails.mbsItem18216UsedToday,
                mbsItem18226UsedToday:
                    patientEncounterDetails.mbsItem18226UsedToday,
                mbsItem17680UsedToday:
                    patientEncounterDetails.mbsItem17680UsedToday,
                scheduledDate: patientEncounterDetails.scheduledDate,
                insuranceType: latestObjDetails.insuranceType ?? insuranceType,
                insuranceState:
                    latestObjDetails.patientInsuranceState ?? insuranceState,
                claimDelayHours:
                    patientEncounterDetails.claimDelayHours ?? Number(0),
                customDueDate: latestObjDetails.customDueDate,
                showClaimDelayHoursAndInvoiceCustomDueDate:
                    itemType == "invoice" &&
                    (!billingData.length ||
                        billingData.every((invoice) => invoice.status == 9)),
                procedureName,
                hospital,
            };

            const { startDate, endDate } = latestObjDetails;

            if (startDate) itemDetails = { ...itemDetails, startDate };

            if (endDate) itemDetails = { ...itemDetails, endDate };

            if (
                shouldInvoiceCopyQuote &&
                !latestObjDetails.hasOwnProperty("billingCodesType")
            ) {
                latestObjDetails.billingCodesType =
                    latestQuoteData.billingCodesType;
                itemsData =
                    latestQuoteData.billingCodesType != 2
                        ? [...latestQuoteDataItems]
                        : latestQuoteDataItems.map((item) => {
                              delete item.id;
                              return item;
                          });
            }

            isModifyItemModalOpen = openModalAfterModify;
        } else {
            itemDetails = {
                ...selectedItem,
                encounterId: patientEncounterDetails.id,
                billingDataId: latestObjDetails.id,
                isUsingAsaItems: isBillingDetailsUsingAsa,
                isUsingFreeTextItems: isBillingDetailsUsingFreeText,
            };
            defaultItem = { ...itemDetails };
            isEditItemModalOpen = openModalAfterModify;
        }
    };

    const _openAddItemModalWithoutLatestBillingData = async () => {
        const {
            patient: { insuranceType },
        } = patientEncounterDetails;

        const isRequiredConsideringTypeA = [16, 17, 25, 26].includes(
            Number(insuranceType),
        );

        const latestQuoteDataItemsType = getBillingItemCodeType(
            latestQuoteData.billingCodesType,
        );

        if (!isRequiredConsideringTypeA) {
            if (!shouldInvoiceCopyQuote) {
                if (allowAsaCodes || allowFreeTextCodes)
                    isAddItemModalWithoutLatestBillingDataModalOpen = true;
                else handleSelectItemToAdd("MBS");
            } else if (
                (latestQuoteDataItemsType == "ASA" && allowAsaCodes) ||
                (latestQuoteDataItemsType == "FreeText" && allowFreeTextCodes)
            )
                handleSelectItemToAdd(latestQuoteDataItemsType);
            else handleSelectItemToAdd("MBS");
        } else {
            isTypeA = await _checkInsuranceTypeIsTypeA();

            if (!shouldInvoiceCopyQuote) {
                if (allowAsaCodes || allowFreeTextCodes)
                    isAddItemModalWithoutLatestBillingDataModalOpen = true;
                else handleSelectItemToAdd("MBS");
            } else if (
                (latestQuoteDataItemsType == "ASA" && allowAsaCodes) ||
                (latestQuoteDataItemsType == "FreeText" && allowFreeTextCodes)
            )
                handleSelectItemToAdd(latestQuoteDataItemsType);
            else handleSelectItemToAdd("MBS");
        }
    };

    const _checkAllowAsaCodes = (itemType, encounter = {}) => {
        if (!encounter || isObjectEmpty(encounter)) return false;

        const {
            patient: { insuranceType, insuranceId, state },
        } = encounter;

        const isLatestQuoteData = itemType == "quote";

        if (
            isLatestQuoteData &&
            ((insuranceType == 10 && insuranceId == 0) || insuranceType == 15 || insuranceType == 18)
        )
            return true;
        else if (!isLatestQuoteData) {
            if (
                (insuranceType == 10 && insuranceId == 0) ||
                insuranceType == 15 ||
                insuranceType == 18 || // 3rd party
                insuranceType == 26 ||
                insuranceType == 30 //pip
            )
                return true;
            // This bit of check is done in checking if encounter isTypeA or not
            // if (
            //     (insuranceType == 16 || insuranceType == 17) &&
            //     ["NSW", "WA", "ACT", "NT", "TAS"].includes(state)
            // )
            //     return true;
            if (insuranceType == 16 || insuranceType == 17) return true;
            else return false;
        } else return false;
    };

    const _closeAddItemModalWithoutLatestBillingData = () => {
        isAddItemModalWithoutLatestBillingDataModalOpen = false;

        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            itemType: "",
        };
    };

    const _openAddBillingRuleModal = () => {
        isAddBillingRuleModalOpen = true;

        if (latestQuoteData.billingType) {
            const {
                actualGap,
                billingType,
                calculationAmount,
                oopType,
                patientPaysTotalFee,
                totalFee,
            } = latestQuoteData;

            copyLatestQuoteBillingRule = {
                ...latestObjDetails,
                actualGap,
                billingType,
                calculationAmount,
                oopType,
                patientPaysTotalFee,
                totalFee,
            };
        }
    };

    const _getPatientAge = () => {
        const { patient, scheduledDate } = patientEncounterDetails;
        const birthDate = new Date(patient.birthDate);

        const date = scheduledDate ? new Date(scheduledDate) : new Date();

        if (isNaN(birthDate.getTime())) {
            console.warn("Invalid birthDate");
            return null;
        }

        let age = date.getFullYear() - birthDate.getFullYear();

        if (
            date.getMonth() < birthDate.getMonth() ||
            (date.getMonth() === birthDate.getMonth() &&
                date.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    };

    const _closeModifyItemModal = () => {
        isModifyItemModalOpen = false;

        _resetDefaultBillingItem();
    };

    const _closeEditItemModal = () => {
        isEditItemModalOpen = false;

        _resetDefaultBillingItem();
    };

    const _closeAddBillingRuleModal = async (response = {}) => {
        isAddBillingRuleModalOpen = false;

        if (!isObjectEmpty(response)) {
            await reloadBillingDetails(false, response);

            confirmRegenerate({ skipRegenerateAndCalculate: true });
           
        }
    };

    const _openRegenerateModal = () => (isShowRegenerateModal = true);

    const _closeRegenerateModal = async (shouldReload = false) => {
        isShowRegenerateModal = false;

        if (shouldReload) await reloadBillingDetails();

        __BackupItemsData__ = [...itemsData];
    };

    const _checkInsuranceTypeIsTypeA = async () => {
        const {
            patient: { insuranceType, insuranceState },
            patientEncounterWorkCoverData,
            patientEncounterTrafficAccidentData,
        } = patientEncounterDetails;

        if (insuranceTypesAllowAsaItem.includes(Number(insuranceType)))
            return false;

        if (insuranceTypesMayAllowAsaItem.includes(Number(insuranceType))) {
            // if (!isObjectEmpty(latestObjDetails))
            //     return !latestObjDetails.usingAmaRates;

            // const defaultStates = ["VIC", "QLD", "SA"];
            const defaultStates = ["VIC", "SA"];

            const notAllowAsaStatesArr =
                insuranceType == 16
                    ? ([
                          ...$mbsToAsaDisallowInsuranceTypeByState["WorkCover"],
                      ] ?? defaultStates)
                    : ([
                          ...$mbsToAsaDisallowInsuranceTypeByState[
                              "TrafficAccident"
                          ],
                      ] ?? defaultStates);

            if (insuranceType == 16) {
                if (patientEncounterWorkCoverData) {
                    const {
                        employer,
                        employerEmail,
                        workCoverAuthority,
                        claimId,
                    } = patientEncounterWorkCoverData;

                    // no claimId means user doesn't have a claimId and ticked that "I don't have ..." checkbox to save encounter
                    // this case allow select both MBS and ASA items
                    if (!claimId && !workCoverAuthority) return false;
                    else if (workCoverAuthority) {
                        const selectedInsurer =
                            await apiMethods.getMethod.getWorkCoverAuthorityById(
                                workCoverAuthority,
                            );

                        if (!selectedInsurer) return true;
                        else return !selectedInsurer.amaRates;
                    } else if (employer || employerEmail) {
                        return notAllowAsaStatesArr.includes(insuranceState);
                    } else return false;
                } else return false;
            }

            // there is no optional for TAC insurance, skip claimId or not will always need to select a TAC insurer, so state check is mandatory
            if (insuranceType == 17)
                return notAllowAsaStatesArr.includes(insuranceState);
        }

        // provider is preferredDefence, can ONLY select MBS and NOT allowed to change gap
        if (insuranceType == 25) return true;

        // can select MBS or ASA and are allowed to change the unit value
        if (insuranceType == 26) return false;

        return false;
    };

    const _openDeleteModal = () => (isShowDeleteModal = true);

    const _closeDeleteModal = () => {
        isShowDeleteModal = false;

        _resetDefaultBillingItem();
    };

    const _openConfirmActionModal = (actionType = "", ...args) => {
        if (!actionType) return;

        if (!scenarioOneFulfilled) {
            if (actionType == "itemChange") saveBillingItem(...args);
            else if (actionType == "billingRuleChange")
                saveBillingRule(...args);
            _closeConfirmActionModal();
            return;
        }

        // Do NOT show confirm modal if all relevant quotes/invoices are cancelled
        isConfirmActionModalOpen = billingData.some((bd) => bd.status != 9);

        if (actionType == "itemChange")
            confirmAction = () => saveBillingItem(...args);
        else if (actionType == "billingRuleChange")
            confirmAction = () => saveBillingRule(...args);

        if (!isConfirmActionModalOpen) confirmChangeItemsOrBillingRules();
    };

    const _closeConfirmActionModal = () => {
        isConfirmActionModalOpen = false;
        confirmAction = () => {};
    };

    const _openSelectPresetModal = () => {
        isSelectPresetModalOpen = true;
        _openModifyItemModal(true, false);
    };

    const _openConfirmSelectPresetModal = () =>
        (isConfirmSelectPresetModalOpen = true);

    const _closeSelectPresetModal = () => {
        isSelectPresetModalOpen = false;
        isConfirmSelectPresetModalOpen = false;
        selectedPreset = {};
    };

    const _openSkipQuoteConfirmModal = () => (toggleSkipQuoteModalOpen = true);

    const _closeSkipQuoteConfirmModal = () =>
        (toggleSkipQuoteModalOpen = false);

    const getAllowedPresets = () => {
        //uncomment this if we want to allow adding presets based on what encounter allows all the time, not based on billingcodestype
        if(allowAsaCodes) return doctorPresets; //if allow asa codes return all presets
        else return doctorPresets.filter((p) => !p.useAsaCodes); //else return only mbs presets

       /*  if(latestObjDetails.hasOwnProperty("billingCodesType")) {
            //return presets based on billing codes type
            if(latestObjDetails.billingCodesType == 0) {
                //return mbs presets
                return doctorPresets.filter((p) => !p.useAsaCodes);
            } else if (latestObjDetails.billingCodesType == 1){
                //return asa presets
                return doctorPresets.filter((p) => p.useAsaCodes);
            }
        }
        else {
            if(allowAsaCodes) return doctorPresets; //if allow asa codes return all presets
            else return doctorPresets.filter((p) => !p.useAsaCodes); //else return only mbs presets

        } */
        return [];
    }

    const getItemsListString = () => {
        const itemCodes = displayItemsData.map((item) => {
            if (isBillingDetailsUsingAsa) return item.itemCode;
            else return item.itemNum;
        });

        return itemCodes.join(", ");
    }

    const encounterHasQuotePrepayWithGap = () => {
        if(patientEncounterDetails.estimates.some((est) => est.status != 9 && est.actualGap > 0)) {
            var estimate = patientEncounterDetails.estimates.find((est) => est.status != 9 && est.actualGap > 0);
            var billingData = patientEncounterDetails.billingData.find((bd) => bd.id == estimate.patientEncounterBillingDataId);
            if(billingData)
                return billingData.oopType == 0; //prepay
        }
        return false;
    }
   
</script>

<CardLayout
    title={`${itemType == "quote" ? "Quote" : "Invoice"} Items`}
    {btnOptions}
>
    {#if __BackupItemsData__.length}
        {#if isRegenerating}
            <PageLoading />
        {:else}
            <div class="w-full overflow-x-auto">
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={[
                        ...displayItemsData,
                        getSumOfColumnValues(
                            displayItemsData,
                            latestObjDetails.addGST,
                        ),
                    ]}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>
                    No items currently under this {itemType
                        .toString()
                        .toLowerCase()}
                </p>
            </div>
        </div>
    {/if}
</CardLayout>

{#if isModifyItemModalOpen}
    {#if isBillingDetailsUsingAsa}
        <CodeSelectorAsa
            isSelectorOpen={isModifyItemModalOpen}
            {itemType}
            {hasActiveQuoteOrInvoice}
            clearItems={clearBillingData}
            procedureName={patientEncounterDetails.procedureName}
            doctorId={patientEncounterDetails.doctorId}
            itemCodeObj={itemDetails}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) =>
                _openConfirmActionModal("itemChange", ...args)}
            {changeItemType}
            {isTypeA}
            isSavingItems={isUpdatingItems}
            {allowFreeTextCodes}
        />
    {:else if isBillingDetailsUsingFreeText}
        <CodeSelectorFreeText
            isSelectorOpen={isModifyItemModalOpen}
            itemType={itemType}
            procedureName={patientEncounterDetails.procedureName}
            {patientEncounterDetails}
            itemCodeObj={itemDetails}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) =>
                _openConfirmActionModal("itemChange", ...args)}
            {changeItemType}
            {isTypeA}
            {allowAsaCodes}
            isSavingItems={isUpdatingItems}
        />
    {:else}
        <CodeSelectorMbs
            isSelectorOpen={isModifyItemModalOpen}
            itemCodeObj={itemDetails}
            {hasActiveQuoteOrInvoice}
            clearItems={clearBillingData}
            {itemType}
            {latestObjDetails}
            procedureName={patientEncounterDetails.procedureName}
            doctorId={patientEncounterDetails.doctorId}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) =>
                _openConfirmActionModal("itemChange", ...args)}
            {changeItemType}
            {isTypeA}
            isSavingItems={isUpdatingItems}
            {allowAsaCodes}
            {allowFreeTextCodes}
        />
    {/if}
{/if}

{#if isEditItemModalOpen}
    <PatientEncounterModifyItemModal
        isModifyItemModalOpen={isEditItemModalOpen}
        {isUpdatingItems}
        {itemDetails}
        {defaultItem}
        {saveBillingItem}
        closeModal={_closeEditItemModal}
    />
{/if}

{#if isAddBillingRuleModalOpen}
    <PatientEncounterAddBillingRuleModal
        bind:this={billingRuleModalRef}
        bind:patientEncounterDetails
        {doctorBillingRules}
        {isAddBillingRuleModalOpen}
        closeModal={_closeAddBillingRuleModal}
        isAsaItems={shouldInvoiceCopyQuote
            ? latestQuoteData.billingCodesType == 1
            : isBillingDetailsUsingAsa}
        {itemsData}
        latestObjDetails={shouldInvoiceCopyQuote
            ? copyLatestQuoteBillingRule
            : latestObjDetails}
        {paymentsData}
        saveBillingRule={(...args) =>
            _openConfirmActionModal("billingRuleChange", ...args)}
    />
{/if}

<NotificationModal
    isModalOpen={isShowRegenerateModal}
    isConfirming={isRegenerating}
    closeModal={() => _closeRegenerateModal(true)}
    showConfirmBtn={false}
    closeModalFunction={() => _closeRegenerateModal(true)}
    btnOptions={[
        {
            text: isRegenerating ? "Generating" : "Confirm",
            loading: isRegenerating,
            function: () => confirmRegenerate({ isRecalculate: true }),
        },
    ]}
>
    Please confirm you would like to update the item numbers and re-calculate
    the associated billing details.
</NotificationModal>

<NotificationModal
    title="Remove Item"
    isModalOpen={isShowDeleteModal}
    isConfirming={isDeletingItem}
    closeModal={() => saveBillingItem(selectedItem, "delete")}
    confirmAction={_closeDeleteModal}
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    cancelText={isDeletingItem ? "Removing" : "Remove"}
    closeModalFunction={_closeDeleteModal}
    isReverseBtns
>
    <p class="whitespace-pre-line">Remove this item?</p>
</NotificationModal>

<NotificationModal
    title="Select Items Type"
    isModalOpen={isAddItemModalWithoutLatestBillingDataModalOpen}
    closeModal={_closeAddItemModalWithoutLatestBillingData}
    showConfirmBtn={false}
    closeModalFunction={_closeAddItemModalWithoutLatestBillingData}
>
    <div class="flex flex-col flex-1 gap-y-2 p-4">
        <div class="flex flex-1 gap-x-2 items-center justify-around">
            <button
                class="btn btn-md w-36 btn-secondary"
                on:click|preventDefault={() => handleSelectItemToAdd("MBS")}
            >
                MBS
            </button>

            {#if allowAsaCodes}
                <button
                    class="btn btn-md w-36 btn-secondary"
                    on:click|preventDefault={() => handleSelectItemToAdd("ASA")}
                >
                    ASA
                </button>
            {/if}

            {#if allowFreeTextCodes}
                <button
                    class="btn btn-md w-36 btn-secondary"
                    on:click|preventDefault={() =>
                        handleSelectItemToAdd("FreeText")}
                >
                    Free Text
                </button>
            {/if}
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="Notice"
    isModalOpen={isConfirmActionModalOpen}
    closeModal={_closeConfirmActionModal}
    confirmAction={confirmChangeItemsOrBillingRules}
    cancelText="Cancel"
    submitText="Confirm"
    closeModalFunction={_closeConfirmActionModal}
>
    <p class="leading-6">
        {#if scenarioTwoFulfilled}
            A paid {itemType} already exists. Updating the billing information will
            cancel the original {itemType}, create a new one and move all the
            payments to it.
        {:else}
            An unpaid {itemType} already exists. Updating the billing information
            will also update and regenerate the associated {itemType}.
        {/if}
    </p>

    <p class="leading-6">Do you want to proceed?</p>
</NotificationModal>

<NotificationModal
    title="Select preset to apply"
    isModalOpen={isSelectPresetModalOpen}
    closeModal={_closeSelectPresetModal}
    isConfirming={isApplyingPreset}
    cancelText="Cancel"
    showConfirmBtn={false}
    contentClass="p-6 flex flex-col overflow-visible"
    closeModalFunction={_closeSelectPresetModal}
    btnOptions={[
        {
            text: "Select",
            loading: isObjectEmpty(selectedPreset) || isApplyingPreset,
            function: () => handleSelectPreset(),
        },
    ]}
>
    <div class="flex flex-1 flex-col gap-y-4">
        {#if !doctorPresets || !doctorPresets.length}
            No presets found
        {:else}
            <div class="flex items-center w-full">
                <select
                    class="input input-bordered input-sm flex-1"
                    on:change|preventDefault={async (e) => {
                        const {
                            target: { value },
                        } = e;
                        selectedPresetEncounterRule = {}
                        selectedPreset =
                            doctorPresets.find(
                                (preset) => preset.id == value,
                            ) ?? {};

                        if (selectedPreset.useAsaCodes) {
                            await cacheAsaItems();
                            selectedPreset.asaCodes =
                                selectedPreset.asaCodes.map((code) => {
                                    if (code.tooltip) return code;

                                    const targetCode = $asaAllItemsList.find(
                                        (item) =>
                                            item.itemCode == code.itemCode &&
                                            item.subItemCode ==
                                                code.subItemCode,
                                    );

                                    return {
                                        ...code,
                                        tooltip: targetCode
                                            ? targetCode.description
                                            : "",
                                    };
                                });
                        } else if (
                            selectedPreset?.hasOwnProperty("useAsaCodes") &&
                            !selectedPreset.useAsaCodes
                        ) {
                            await cacheMbsItems();
                            selectedPreset.mbsCodes =
                                selectedPreset.mbsCodes.map((code) => {
                                    if (code.tooltip) return code;

                                    const targetCode = $mbsAllItemsList.find(
                                        (item) =>
                                            item.itemNum == code.itemNum &&
                                            item.subItemNum == code.subItemNum,
                                    );

                                    return {
                                        ...code,
                                        tooltip: targetCode
                                            ? targetCode.description
                                            : "",
                                    };
                                });
                        }

                        selectedPreset.billingRules =
                            selectedPreset.billingRules.map((br) => {
                                if (br.tooltip) return br;

                                const targetDoctorBillingRule =
                                    doctorBillingRules.find(
                                        (dbr) => dbr.id == br.billingRuleId,
                                    );

                                return {
                                    ...br,
                                    tooltip: targetDoctorBillingRule
                                        ? `${getOOPType(targetDoctorBillingRule.oopType)} ${getAmountInCurrency(targetDoctorBillingRule.amount)} ${getBillingRuleType(targetDoctorBillingRule.type)}`
                                        : "",
                                };
                            });
                        
                        if(selectedPreset && selectedPreset.billingRules?.length) {
                            //get applicable rule for this encounter
                            selectedPresetEncounterRule = await getSelectedPresetApplicableRuleForEncounter(selectedPreset, patientEncounterDetails, latestObjDetails);
                        }
                    }}
                >
                    <option value="" selected disabled>
                        Select preset to apply
                    </option>
                    {#each getAllowedPresets() as preset}
                        <option value={preset.id}>{preset.name}</option>
                    {/each}
                </select>
            </div>

            {#if !isObjectEmpty(selectedPreset)}

                <div class="flex items-center w-full gap-x-2">
                    <p class="title">Items numbers:</p>

                    <div class="flex flex-wrap flex-1 gap-x-2">
                        {#each selectedPreset[selectedPreset.useAsaCodes ? "asaCodes" : "mbsCodes"] as item}
                            <div
                                class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                data-tip={item.tooltip ?? ""}
                            >
                                {item[
                                    selectedPreset.useAsaCodes
                                        ? "itemCode"
                                        : "itemNum"
                                ]}{item[
                                    selectedPreset.useAsaCodes
                                        ? "subItemCode"
                                        : "subItemNum"
                                ]
                                    ? `-${item[selectedPreset.useAsaCodes ? "subItemCode" : "subItemNum"]}`
                                    : ""}
                            </div>
                        {/each}
                        {#if selectedPreset.procedureLength}
                            <div
                                class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                data-tip={`${selectedPreset.procedureLength} minutes`}
                            >
                                Time
                            </div>
                        {/if}
                    </div>
                </div>

                <div
                    class="flex flex-col gap-y-4 items-center w-full gap-x-4 max-h-[300px] overflow-y-auto"
                >
                    {#if !isObjectEmpty(selectedPresetEncounterRule)}
                        <div
                            class="flex flex-col gap-y-2 w-full cursor-default"
                        >
                            <div class="flex w-full items-center gap-x-4">
                                <p class="title">Provider:</p>
                                <p>
                                    {doctorProviders.find(
                                        (provider) =>
                                            provider.id ==
                                            selectedPresetEncounterRule.doctorProviderId,
                                    )?.number ?? "-"}
                                </p>
                            </div>

                            <div class="flex w-full items-center gap-x-4">
                                <p class="title">Billing rule:</p>
                                <p
                                    class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                    data-tip={selectedPresetEncounterRule.tooltip ?? ""}
                                >
                                    {doctorBillingRules.find(
                                        (br) =>
                                            br.id == selectedPresetEncounterRule.billingRuleId,
                                    )?.name ?? "-"}
                                </p>
                            </div>
                        </div>
                    {/if}
                </div>
            {/if}
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    title="Notice"
    isModalOpen={isConfirmSelectPresetModalOpen}
    closeModal={_closeSelectPresetModal}
    confirmAction={confirmSelectPreset}
    cancelText="Cancel"
    submitText="Confirm"
    closeModalFunction={_closeSelectPresetModal}
>
    <div class="p-6">
        <p class="leading-6">
            {#if itemType == 'invoice'}
                <InfoIcon tooltipClass="w-[250px]" info={'When creating a quote, items are copied to the invoice, or you may have entered items on the invoice already.'}></InfoIcon>
            {/if}
            The {itemType} already contains items {getItemsListString()}. If you proceed, the {itemType} items will be replaced with the preset item numbers
            {#if (!isObjectEmpty(selectedPresetEncounterRule) && selectedPresetEncounterRule.billingRuleId)}
                {#if itemType == "quote" || (itemType == "invoice" && encounterHasQuotePrepayWithGap() && !selectedPresetEncounterRule.tooltip.startsWith("Pre Pay"))}
                    and the billing rule will be updated to {selectedPresetEncounterRule.tooltip}
                {/if}
            {/if}
        </p>
       
        {#if itemType == "invoice" && encounterHasQuotePrepayWithGap() && !isObjectEmpty(selectedPresetEncounterRule) && selectedPresetEncounterRule.billingRuleId}
            <br/>
            {#if selectedPresetEncounterRule.tooltip.startsWith("Pre Pay")}
                <p class="text-warning">
                    The preset billing rule is for prepayment, therefore the preset billing rule will not be applied. If you wish to use this preset for invoice billing, please change the preset billing type to Postpay.
                </p>
            {:else}
                <p class="text-warning">
                    If the preset billing rule results in a higher or lower out of pocket, this action will result in a topup payment or refund being required for the invoice.
                </p>
            {/if}
        {/if}
        <br/>
        <p class="leading-6">Do you want to proceed?</p>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={toggleSkipQuoteModalOpen}
    title={patientEncounterDetails?.skipQuote
        ? "Requote the patient"
        : "Skip Quote"}
    closeModalFunction={_closeSkipQuoteConfirmModal}
    isConfirming={isChangingSkipQuote}
    showCancelBtn={false}
    showConfirmBtn={false}
    btnOptions={[
        {
            text: "Cancel",
            function: () => _closeSkipQuoteConfirmModal(),
            class: patientEncounterDetails?.skipQuote
                ? "btn-accent"
                : "btn-secondary",
            loading: isChangingSkipQuote,
        },
        {
            text: patientEncounterDetails?.skipQuote ? "Requote" : "Skip Quote",
            function: () => confirmToggleSkipQuote(),
            class: patientEncounterDetails?.skipQuote
                ? "btn-secondary"
                : "btn-error",
            loading: isChangingSkipQuote,
        },
    ]}
>
    <p class="leading-6 p-4">
        {#if patientEncounterDetails?.skipQuote}
            You are about the requote the patient.
        {:else}
            Skip quote for this encounter?
        {/if}
    </p>
</NotificationModal>

<style>
    .title {
        width: 120px;
    }
</style>
